import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { filter, take, timer } from 'rxjs';
import { GA_EVENTS, GA_PROPS, SOFT_SKILLS } from 'src/app/core/const.variables';
import { DashboardProxy } from 'src/app/core/store/dashboard/dashboard.proxy';
import { scaleInOut } from '../../animations/scale-in-out.animation';
import { opacityInOut } from '../../animations/opacity-in-out.animation';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { HelperService } from 'src/app/core/services/helper.service';
import { ApiService } from 'src/app/core/services/api.service';
import { SourcingType } from 'src/app/core/sourcing-type.enum';
import { CandidateProfileEventType } from 'src/app/core/services/recruiter-activity-analytics.service.type';

type CandidateCvFormat = 'pdf' | 'docx';

@Component({
  selector: 'match-details',
  templateUrl: 'match-details.component.html',
  styleUrls: ['match-details.component.scss'],
  animations: [ opacityInOut, scaleInOut ]
})
export class MatchDetailsComponent implements OnInit {

  @Input() matchId = null;
  @Input() isVacancy = false;
  @Output() onClose = new EventEmitter<any>();
  @Output() onAnalyticsEvent = new EventEmitter<CandidateProfileEventType>();
  @Input() leastFoundSkills = [];
  @Input() sourcingType = null;
  @Input() vacancySkillNames: Set<string> | null = null;

  trackAnalyticsEvent(eventType: CandidateProfileEventType): void {
    this.onAnalyticsEvent.emit(eventType);
  }

  currentTab = 'info';
  talentCvData = null;
  matchScore = null;
  hardSkillsCommon = null;
  hardSkillsSimilar = null;
  softSkills = null;
  matchDetails = null;
  additionalData = null;
  showDownloadMenu = false;
  cvDownloadInProgress = false;
  originalResumeDownloadInProgress = false;
  touchedSections = sessionStorage.getItem('touched') ? JSON.parse(sessionStorage.getItem('touched')) : [];
  SourcingType = SourcingType;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private dashboardProxy: DashboardProxy,
    private gaService: GoogleAnalyticsService,
    private helperService: HelperService,
    private apiService: ApiService
  ) {
  }

  async ngOnInit() {
   
    let loadTime = 0;
    const timeSub = timer(0, 1).subscribe(time => loadTime = time)
    this.dashboardProxy.dropVacancyTalentDetails();

    await this.dashboardProxy.getMatchById(this.matchId);
    this.onAnalyticsEvent.emit(CandidateProfileEventType.CandidateMatchDetailsOpened);
    this.dashboardProxy.matchDetails$.pipe(filter(data => !!data), take(1)).subscribe(async matchDetails => {
      this.helperService.blockScroll$.next(true);
      this.matchDetails = matchDetails;
      this.matchScore = {
        totalMatch: matchDetails.totalMatch.toFixed(),
        hardSkillsMatch: matchDetails.hardSkillsMatch.toFixed(),
        softSkillsMatch: matchDetails.softSkillsMatch.toFixed()
      };
       const talentResult = await this.dashboardProxy.getTalentById(this.matchDetails.userId, this.matchDetails.vacancyId);
      const talent = (talentResult as any)?.payload;
       this.talentCvData = talent;
       const talentHardSkills = talent?.hardSkills || [];
      this.hardSkillsCommon = this.prepareHardSkills(matchDetails.additionalInfo,talentHardSkills);
      this.hardSkillsSimilar = this.prepareHardSkills(matchDetails.additionalInfo, talentHardSkills,false);
      this.softSkills = this.prepareSoftSkills(matchDetails.additionalInfo);
      this.selectTab('info');
      timeSub.unsubscribe();
      this.gaService.gtag('event', GA_EVENTS.ShowMatchDetails, { 
        [GA_PROPS.LoadTime]: (loadTime / 1000).toFixed(2),
        [GA_PROPS.LoadTimeDim]: (loadTime / 1000).toFixed(2)
      });
    });
  }

  selectTab(param: string, fromUser = false) {
    this.currentTab = param;
    if (fromUser) {
      this.showDownloadMenu = false;
    }
    if (this.currentTab === 'info' ) {
      this.isVacancy ? this.dashboardProxy.getVacancyById(this.matchDetails.vacancyId) : this.dashboardProxy.getTalentById(this.matchDetails.userId, this.matchDetails.vacancyId);
      (this.isVacancy ? this.dashboardProxy.vacancyDetails$ : this.dashboardProxy.talentDetails$)
        .pipe(filter(data => !!data), take(1)).subscribe(data => {
          this.additionalData = data;
          if (!this.isVacancy) {
            this.talentCvData = data;
          }
        });
    }
    setTimeout(() => {
      const element = document.querySelectorAll('.is-tab-active');
      if (element && element[0]) {
        element[0].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    });
  }

  close() {
    this.showDownloadMenu = false;
    this.helperService.blockScroll$.next(false);
    this.onClose.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.showDownloadMenu) {
      return;
    }

    const target = event.target as Node | null;
    const downloadContainer = this.elementRef.nativeElement.querySelector('.match-details__download');

    if (downloadContainer && target && !downloadContainer.contains(target)) {
      this.showDownloadMenu = false;
    }
  }

  toggleDownloadMenu(event: MouseEvent) {
    event.stopPropagation();
    this.showDownloadMenu = !this.showDownloadMenu;
  }

  downloadCv(format: CandidateCvFormat) {
    this.showDownloadMenu = false;
    if (!this.matchDetails || this.cvDownloadInProgress) {
      return;
    }

    this.onAnalyticsEvent.emit(CandidateProfileEventType.CandidateCvDownloaded);
    this.cvDownloadInProgress = true;
    this.apiService.downloadMatchedProfile(this.matchId, format).pipe(take(1)).subscribe({
      next: response => {
        if (!response.body) {
          return;
        }

        const contentType = response.headers?.get('content-type') || response.body.type || '';
        if (!this.isCvContentType(contentType, format)) {
          console.error('Candidate CV download failed. The server returned a non-file response.');
          return;
        }

        this.saveCvFile(response.body, this.getDownloadFileName(response, format));
      },
      error: () => {
        this.cvDownloadInProgress = false;
      },
      complete: () => {
        this.cvDownloadInProgress = false;
      }
    });
  }

  handleDownloadResumeForLiveCandidates(): void {
    const candidateId = this.matchDetails?.userId;
    const candidateName = this.talentCvData?.name || this.matchDetails?.name;

    if (this.originalResumeDownloadInProgress || !candidateId) return;

    this.onAnalyticsEvent.emit(CandidateProfileEventType.CandidateCvDownloaded);
    this.originalResumeDownloadInProgress = true;

    this.apiService.downloadOriginalResume(candidateId).pipe(take(1)).subscribe({
      next: response => {
        if (!response.body) {
          return;
        }

        const fileName = this.getResumeDownloadFileName(response, candidateName);
        this.saveResumeFile(response.body, fileName);
      },
      error: (err) => {
        console.error('Error downloading resume:', err);
        this.originalResumeDownloadInProgress = false;
      },
      complete: () => {
        this.originalResumeDownloadInProgress = false;
      }
    });
  }

  private saveResumeFile(blob: Blob, fileName: string): void {
    this.saveCvFile(blob, fileName);
  }

  private getResumeDownloadFileName(response: any, candidateName?: string): string {
    const contentDisposition = response.headers?.get('content-disposition') || '';
    const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
    const asciiMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(contentDisposition);
    const responseFileName = decodeURIComponent((utf8Match?.[1] || asciiMatch?.[1] || '').trim());
    if (responseFileName) {
      return responseFileName;
    }

    const safeName = (candidateName || 'candidate').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'candidate';

    return `${safeName}-resume`;
  }
  
  private saveCvFile(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private getDownloadFileName(response, format: CandidateCvFormat): string {
    const contentDisposition = response.headers?.get('content-disposition') || '';
    const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
    const asciiMatch = /filename\s*=\s*"?([^\";]+)"?/i.exec(contentDisposition);
    const responseFileName = decodeURIComponent((utf8Match?.[1] || asciiMatch?.[1] || '').trim());
    if (responseFileName) {
      return responseFileName;
    }

    const candidateName = this.talentCvData?.name || this.matchDetails?.name || 'candidate';
    return `${this.normalizeFileName(candidateName)}-atalef-cv.${format}`;
  }

  private normalizeFileName(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'candidate';
  }

  private isCvContentType(contentType: string, format: CandidateCvFormat): boolean {
    return format === 'pdf'
      ? contentType.includes('application/pdf')
      : contentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
  }

  setTouched(param) {
    if (!this.touchedSections.includes(param)) {
      this.touchedSections.push(param);
      sessionStorage.setItem('touched', JSON.stringify(this.touchedSections));
    }
  }

  private prepareHardSkills(data, talentHardSkills, common = true) {
    
  if (data && data.detailedHardSkillsMatch?.length) {
    const toReturn = [];
    data.detailedHardSkillsMatch.forEach(branch => {
      if (branch.skills?.length) {
        const filteredSkills = branch.skills.filter(skill => common ? !skill.closeSkill : skill.closeSkill);

        if (filteredSkills.length) {
          const mappedSkills = filteredSkills.map(skill => {
            // find candidate’s skill
            const talentBranch = talentHardSkills.find(tb =>
              tb.name?.toLowerCase() === branch.name?.toLowerCase() ||
              tb.skills?.some(ts => ts.name?.toLowerCase() === skill.name?.toLowerCase())
            );

            const talentSkill = talentBranch?.skills?.find(ts => 
              
               common ? ts.name?.toLowerCase() === skill.name?.toLowerCase() : ts.name?.toLowerCase() === skill.closeSkillName?.toLowerCase()

            );
            return {
              ...skill,
              vacancyExp: skill.years ?? 0,
              talentExp: talentSkill?.years ?? 0,
              expertiseRequired: skill.experience ?? 0,
              talentExpertise: talentSkill?.experience ?? 0
            };
          });

          // Sort skills by skillMatch (high to low) and level of importance
          const sortedSkills = mappedSkills.sort((a, b) => {
            // First, sort by skillMatch (descending)
            if (b.skillMatch !== a.skillMatch) {
              return b.skillMatch - a.skillMatch;
            }

            // If skillMatch , sort by talentExpertise - level of importance(descending)
            return b.talentExpertise - a.talentExpertise;
          });

          toReturn.push({ ...branch, skills: sortedSkills });
        }
      }
    });

    // Sort branches by average skillMatch (descending)
    const sortedBranches = toReturn.sort((a, b) => {
      const avgMatchA = a.skills.reduce((sum, skill) => sum + skill.skillMatch, 0) / a.skills.length;
      const avgMatchB = b.skills.reduce((sum, skill) => sum + skill.skillMatch, 0) / b.skills.length;
      return avgMatchB - avgMatchA;
    });

    return sortedBranches.length ? sortedBranches : null;
  }
  return null;
}

  private prepareSoftSkills(data) {
    if (data && data.detailedSoftSkillsMatch && data.detailedSoftSkillsMatch.length) {
      return SOFT_SKILLS.map(item => ({
        ...item,
        skillMatch: data.detailedSoftSkillsMatch.find(val => item.name === val.name).skillMatch
      }));
    }
    return null;
  }

}
